import { useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Edit3, History } from 'lucide-react';
import {
  DashboardDetailsPageSkeleton,
  DashboardDetailsTab,
  DashboardDetailsTabList,
  DashboardDetailsHeader,
  type DashboardDetailsMetadataEntry,
  dashboardPageTitleClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, DropdownMenuItem, Tabs } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useShigoSession } from '../../../../../lib/useShigoSession';
import { canReadIntegrationJourney } from '../../../../auth/accessPolicy';
import { formatUsd } from '../../../../../lib/format';
import { dashboardT } from '../../../../../lib/messages';
import { ChangelogDialog } from '../../../../changelog/components/ChangelogDialog';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useIntegrationQuery } from '../../hooks';
import { IntegrationDetailsTab, type Integration } from '../../model';

export function IntegrationDetailsPage(props: {
  demandPartnerId: string;
  integrationId: string;
  children: ReactNode;
}) {
  const { session } = useShigoSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { can } = useShigoPermissions();
  const query = useIntegrationQuery(props.demandPartnerId, props.integrationId);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const canEdit = can(ShigoPermission.DemandIntegrationsEdit);
  const canReadChangelog = can(ShigoPermission.DemandIntegrationsChangelogRead);
  const canReadPlacements = can(ShigoPermission.DemandIntegrationPlacementsRead);
  let activeTab = IntegrationDetailsTab.Overview;
  if (pathname.endsWith('/journey')) activeTab = IntegrationDetailsTab.Journey;
  if (pathname.endsWith('/placements')) activeTab = IntegrationDetailsTab.Placements;
  const tabs = [
    {
      value: IntegrationDetailsTab.Overview,
      label: dashboardT('integrations.tabs.overview'),
      to: '/demand/integrations/$integrationId/demand/$demandPartnerId/details/overview' as const,
    },
    {
      value: IntegrationDetailsTab.Journey,
      label: dashboardT('integrations.tabs.journey'),
      to: '/demand/integrations/$integrationId/demand/$demandPartnerId/details/journey' as const,
    },
    ...(canReadPlacements
      ? [
          {
            value: IntegrationDetailsTab.Placements,
            label: dashboardT('integrations.tabs.placements'),
            to: '/demand/integrations/$integrationId/demand/$demandPartnerId/details/placements' as const,
          },
        ]
      : []),
  ].filter(
    (tab) =>
      tab.value !== IntegrationDetailsTab.Journey ||
      Boolean(session && canReadIntegrationJourney(session.user)),
  );

  if (query.isLoading) {
    return (
      <DashboardDetailsPageSkeleton
        label={dashboardT('integrations.loadingDetails')}
        showHeaderAction={canEdit}
        metadataItemCount={4}
        showSecondaryAction={canReadChangelog}
        tabCount={tabs.length}
      />
    );
  }

  return (
    <>
      <main className="min-w-0 overflow-x-clip">
        <Tabs value={activeTab}>
          <div className="border-border bg-card/60 border-b">
            <section className="page-gutter mx-auto flex w-full max-w-[1920px] flex-col gap-4 pt-5">
              <DashboardDetailsHeader
                back={
                  <ReturnBackLink
                    fallback={{ to: '/demand/integrations' }}
                    label={dashboardT('integrations.back')}
                  />
                }
                title={
                  <h1 className={dashboardPageTitleClassName('min-w-0 truncate')}>
                    {query.data?.name ?? dashboardT('integrations.detailsError')}
                  </h1>
                }
                metadata={query.data ? integrationMetadata(query.data) : undefined}
                moreActionsLabel={dashboardT('integrations.moreActions')}
                moreActions={
                  canReadChangelog && query.data ? (
                    <DropdownMenuItem
                      onSelect={() => {
                        setChangelogOpen(true);
                      }}
                    >
                      <History className="size-4" aria-hidden="true" />
                      {dashboardT('integrations.changelog')}
                    </DropdownMenuItem>
                  ) : undefined
                }
                primaryAction={
                  canEdit && query.data ? (
                    <Button asChild variant="ghost" size="sm">
                      <Link
                        to="/demand/integrations/edit/$demandPartnerId/$integrationId"
                        params={{
                          demandPartnerId: props.demandPartnerId,
                          integrationId: props.integrationId,
                        }}
                      >
                        <Edit3 className="size-4" aria-hidden="true" />
                        {dashboardT('integrations.edit')}
                      </Link>
                    </Button>
                  ) : undefined
                }
              />
              <DashboardDetailsTabList aria-label={dashboardT('integrations.detailsTabs')}>
                {tabs.map((tab) => {
                  return (
                    <DashboardDetailsTab key={tab.value} value={tab.value} asChild>
                      <Link
                        to={tab.to}
                        params={{
                          demandPartnerId: props.demandPartnerId,
                          integrationId: props.integrationId,
                        }}
                      >
                        {tab.label}
                        {tab.value === IntegrationDetailsTab.Placements &&
                        query.data?.placementCnt ? (
                          <span className="bg-muted rounded-full px-2 py-0.5 text-xs">
                            {query.data.placementCnt}
                          </span>
                        ) : null}
                      </Link>
                    </DashboardDetailsTab>
                  );
                })}
              </DashboardDetailsTabList>
            </section>
          </div>
          <section className="page-gutter mx-auto w-full max-w-[1920px] min-w-0 py-6">
            {query.isError ? (
              <div className="border-border bg-card space-y-4 rounded-md border p-6">
                <p>{dashboardT('integrations.detailsError')}</p>
                <Button type="button" variant="outline" onClick={() => void query.refetch()}>
                  {dashboardT('common.retry')}
                </Button>
              </div>
            ) : (
              props.children
            )}
          </section>
        </Tabs>
      </main>
      {query.data && changelogOpen ? (
        <ChangelogDialog
          title={dashboardT('integrations.changelogTitle', { name: query.data.name })}
          description={dashboardT('integrations.changelogDescription')}
          scope={{ integrationIds: [query.data.id] }}
          onClose={() => {
            setChangelogOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function integrationMetadata(integration: Integration): DashboardDetailsMetadataEntry[] {
  return [
    {
      label: dashboardT('integrations.demandPartner'),
      value: integration.demandPartner.displayName ?? integration.demandPartner.name ?? '-',
    },
    { label: dashboardT('integrations.integrationType'), value: integration.integrationType },
    {
      label: dashboardT('integrations.floor'),
      value: formatUsd(integration.bidFloor, { maximumFractionDigits: 4 }),
    },
    {
      label: dashboardT('integrations.targetCpm'),
      value: formatUsd(integration.targetCpm, { maximumFractionDigits: 4 }),
    },
  ];
}
